import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@skupreme/shared/ui';
import { AppModalXIcon } from '@elements/Modal';

type PaymentDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Leave it out when the body draws its own heading and close button. */
  title?: ReactNode;
  'aria-label': string;
  /** Match the kit Modal's widths of the same names. */
  size?: 'sm' | 'md' | 'xl';
  children: ReactNode;
};

/*
 * The kit Modal's look without its React Aria overlay. That overlay marks every
 * node added to <body> while it is open as `inert`, and Stripe mounts its 3-D
 * Secure challenge there, so a card that needs one could not be confirmed.
 * Esc and a click on the backdrop close it, as Semantic's modal did.
 */
export function PaymentDialog({
  open,
  onClose,
  title,
  'aria-label': ariaLabel,
  size = 'sm',
  children,
}: PaymentDialogProps) {
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div className="sk:fixed sk:inset-0 sk:z-3000 sk:flex sk:items-center sk:justify-center sk:p-4">
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={onClose}
        className="sk:absolute sk:inset-0 sk:m-0 sk:cursor-default sk:appearance-none sk:border-0 sk:bg-black/25 sk:p-0"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={cn(
          'sk:relative sk:max-h-full sk:w-full sk:overflow-y-auto sk:rounded-card sk:bg-surface sk:p-5',
          'sk:inset-shadow-hairline sk:shadow-card sk:font-sans sk:text-fg',
          { sm: 'sk:max-w-96', md: 'sk:max-w-128', xl: 'sk:max-w-270' }[size],
        )}
      >
        {title ? (
          <header className="sk:mb-4 sk:flex sk:items-center sk:justify-between sk:gap-3">
            <h2 className="sk:m-0 sk:text-base sk:font-semibold">{title}</h2>
            <AppModalXIcon onClick={onClose} />
          </header>
        ) : null}
        {children}
      </div>
    </div>,
    document.body,
  );
}
